import { Link } from "react-router-dom";
import { ArrowRight, HeartHandshake, Gem, Handshake } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/Reveal";
import { FinalCta } from "@/components/FinalCta";
import { useLanguage } from "../i18n";

const VALUES = [
  { icon: HeartHandshake, title: "Trasparenza", desc: "Ogni fase del progetto è spiegata con un linguaggio semplice: sai sempre a che punto siamo e perché." },
  { icon: Gem, title: "Efficacia", desc: "Progettiamo con un obiettivo: creare strumenti digitali utili, belli da vedere e semplici da usare." },
  { icon: Handshake, title: "Fiducia", desc: "Costruiamo relazioni durature, basate sull'ascolto e su un confronto diretto e onesto." },
];

export default function ChiSiamo() {
  const { t } = useLanguage();
  return (
    <>
      <Seo
        title={`${t("Chi siamo")} — Alsera Studio`}
        description={t("Alsera Studio: studio digitale per piccole imprese e attività indipendenti in Svizzera. Soluzioni pratiche, comunicazione chiara e design curato.")}
      />
      <section className="pt-32 pb-20 sm:pt-40" data-testid="chisiamo-hero">
        <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-2">
          <Reveal>
            <p className="eyebrow">{t("Chi siamo")}</p>
            <h1 className="mt-4 text-4xl font-extrabold leading-[1.1] tracking-tight text-antracite sm:text-5xl">
              {t("Uno studio digitale vicino alla tua attività.")}
            </h1>
            <p className="mt-6 text-base leading-relaxed text-antracite/70 sm:text-lg">
              {t("Alsera Studio aiuta attività indipendenti e piccole imprese in Svizzera a migliorare la propria presenza online, con soluzioni digitali pratiche e una comunicazione chiara, senza complicazioni.")}
            </p>
            <p className="mt-4 text-base leading-relaxed text-antracite/70">
              {t("Lavoriamo come un sarto: ascoltiamo, misuriamo, progettiamo e realizziamo soluzioni digitali su misura, costruite intorno alle esigenze reali di ogni cliente.")}
            </p>
          </Reveal>
          <Reveal delay={0.15}>
            <div className="relative">
              <div className="absolute -left-6 -top-6 h-24 w-24 rounded-3xl border border-salvia/50 bg-acqua/70" aria-hidden="true" />
              <img
                src="https://images.pexels.com/photos/326505/pexels-photo-326505.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940"
                alt={t("Alsera Studio — Chi siamo")}
                className="relative aspect-[4/3] w-full rounded-3xl border border-salvia/40 object-cover shadow-xl shadow-petrolio/15"
                data-testid="chisiamo-image"
              />
            </div>
          </Reveal>
        </div>
      </section>

      <section className="noise-overlay bg-acqua/45 py-24 sm:py-28" data-testid="chisiamo-valori">
        <div className="mx-auto max-w-7xl px-5 sm:px-8">
          <Reveal>
            <p className="eyebrow">{t("I nostri valori")}</p>
            <h2 className="mt-4 max-w-2xl text-2xl font-bold tracking-tight text-antracite sm:text-3xl lg:text-4xl">
              {t("Il metodo Alsera.")}
            </h2>
          </Reveal>
          <div className="mt-14 grid gap-6 md:grid-cols-3">
            {VALUES.map((v, i) => (
              <Reveal key={v.title} delay={i * 0.1}>
                <div className="group card-soft h-full p-8 transition-[transform,box-shadow] duration-500 hover:-translate-y-1.5 hover:shadow-xl hover:shadow-petrolio/10" data-testid={`valore-${i}`}>
                  <div className="mb-5 inline-flex rounded-2xl bg-acqua p-3 text-petrolio transition-colors duration-500 group-hover:bg-petrolio group-hover:text-avorio">
                    <v.icon className="h-5 w-5" />
                  </div>
                  <h3 className="text-lg font-bold tracking-tight text-antracite">{t(v.title)}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-antracite/65">{t(v.desc)}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2}>
            <div className="mt-16 flex flex-wrap items-center gap-6">
              <p className="max-w-xl text-base leading-relaxed text-antracite/70">
                {t("Vuoi capire se siamo il partner giusto per il tuo progetto? Il primo passo è una conversazione: semplice, diretta e senza impegno.")}
              </p>
              <Link to="/prenota" className="btn-primary" data-testid="chisiamo-cta">
                {t("Prenota una consulenza")}
                <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      <FinalCta />
    </>
  );
}
